import { LayerId } from "fond/types";

import { formatList, Gate, getMissingLayerNames } from "./gating";
import {
  BASE_DATA_LAYERS,
  BaseDataLayerKey,
  BaseDataMissingLayers,
  BaseDataPlannerLayer,
  BaseDataPresence,
  formatCount,
  GenerationKey,
  isGenerationKey,
} from "./layers";
import { isGeneratedLayerStale, Staleness } from "./staleness";
import { BaseDataTask, getTaskCaption } from "./tasks";

export type BaseDataRowStatus = "done" | "partial" | "required" | "ready" | "unavailable" | "stale" | "loading" | "running" | "failed" | "empty";

export type BaseDataRowState = {
  status: BaseDataRowStatus;
  caption: string;
};

export type BaseDataRowInput = {
  key: BaseDataLayerKey;
  present: BaseDataPresence;
  missing: BaseDataMissingLayers;
  staleness: Staleness;
  /** Whether each generation workflow can run now (see `BaseDataGating.generate`). */
  generate: Record<GenerationKey, Gate>;
  /** What each generation workflow would generate its layer from (see `BaseDataGating.generatedFrom`). */
  generatedFrom: Record<GenerationKey, string>;
  /** Whether the row's latest generation failed and its data hasn't been added since (see `isGenerationFailed`). */
  generationFailed?: boolean;
  /** The task the row shows: one running on this layer, or the row's own failed task (see `getBaseDataRowTasks`). */
  task?: BaseDataTask | null;
  /** The row's planner layers with their data. */
  layers?: readonly BaseDataPlannerLayer[];
};

const capitalise = (text: string): string => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/** Return the feature counts of a layer's planner layers that have data, e.g. ["3 paths", "12 points"]. */
const getCountCaptions = (layers: readonly BaseDataPlannerLayer[] = []): string[] =>
  layers.flatMap(({ hasData, count, units }) => (hasData && count !== null ? [formatCount(count, units)] : []));

const doneCaption = (layers: readonly BaseDataPlannerLayer[] | undefined): string => getCountCaptions(layers).join(" · ") || "Added";

const partialCaption = (key: BaseDataLayerKey, missing: readonly LayerId[], layers: readonly BaseDataPlannerLayer[] | undefined): string =>
  capitalise([...getCountCaptions(layers), `${formatList(getMissingLayerNames(key, missing))} missing`].join(" · "));

/** Return the status and caption of a base data row. */
export const getBaseDataRowState = ({
  key,
  present,
  missing,
  staleness,
  generate,
  generatedFrom,
  generationFailed = false,
  task,
  layers,
}: BaseDataRowInput): BaseDataRowState => {
  const definition = BASE_DATA_LAYERS[key];
  if (task?.status === "running") return { status: task.kind === "generate" ? "running" : "loading", caption: getTaskCaption(task) };
  if (task?.status === "failed") return { status: "failed", caption: getTaskCaption(task) };
  if (generationFailed) return { status: "failed", caption: "Generation failed" };
  if (present[key] && isGeneratedLayerStale(key, staleness)) return { status: "stale", caption: "Out of date" };
  if (present[key]) return { status: "done", caption: doneCaption(layers) };
  if (missing[key].length < definition.layers.length) return { status: "partial", caption: partialCaption(key, missing[key], layers) };
  if (definition.group === "inputs") return { status: "required", caption: "Required" };
  if (isGenerationKey(key)) {
    const gate = generate[key];
    return gate.enabled ? { status: "ready", caption: generatedFrom[key] } : { status: "unavailable", caption: gate.reason };
  }
  if (key === "boundary") return { status: "empty", caption: "Optional · import data from it" };
  return { status: "empty", caption: "Optional" };
};
